import { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { logoutCustomer } from '../store/slices/customerAuthSlice';

// Constants for token expiration handling
const CHECK_INTERVAL_MS = 60 * 1000; // Check every 60 seconds
const WARNING_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes before expiry
const EXPIRY_BUFFER_MS = 30 * 1000; // 30 second buffer for clock skew

// localStorage key for token expiration timestamp
// Note: Only stores expiration TIME, NOT the actual token (tokens are in httpOnly cookies)
export const TOKEN_EXPIRY_KEY = 'customerTokenExpiry';

// BroadcastChannel for multi-tab communication (more reliable than storage events)
// Note: Falls back to storage events if BroadcastChannel is not supported
const AUTH_CHANNEL_NAME = 'customer_auth_channel';
let authChannel: BroadcastChannel | null = null;

// Initialize BroadcastChannel if supported
if (typeof BroadcastChannel !== 'undefined') {
  try {
    authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
  } catch {
    // BroadcastChannel not available (e.g., in some contexts)
    authChannel = null;
  }
}

// Message types for cross-tab communication
export const AUTH_EVENTS = {
  LOGOUT: 'customer_logout',
  TOKEN_REFRESHED: 'token_refreshed',
  REFRESH_STARTED: 'refresh_started', // Task #4236: Signal that refresh is in progress
  REFRESH_FAILED: 'refresh_failed', // Task #4377: Signal that refresh failed
  REFRESH_CANCELLED: 'refresh_cancelled', // Task #4378: Signal that refresh was cancelled by user
} as const;

// Export the auth channel for use in customerAPI.ts (Task #4236)
export const getAuthChannel = (): BroadcastChannel | null => authChannel;

// Global flag to track if token refresh is in progress (shared across instances)
// This prevents race condition between expiration check and token refresh
let isRefreshingToken = false;

/**
 * Sets the global refresh flag to indicate a token refresh is in progress.
 * Call this at the start of token refresh.
 */
export const setTokenRefreshing = (refreshing: boolean): void => {
  isRefreshingToken = refreshing;
};

/**
 * Gets the current refresh status.
 * @returns true if token refresh is in progress
 */
export const getIsRefreshing = (): boolean => {
  return isRefreshingToken;
};

// Listeners for refresh state changes (Task #4377)
type RefreshStateListener = (isRefreshing: boolean, error?: string | null) => void;
const refreshStateListeners: Set<RefreshStateListener> = new Set();

/**
 * Subscribe to refresh state changes.
 * Used by useRefreshState hook for UI updates.
 */
export const subscribeToRefreshState = (listener: RefreshStateListener): (() => void) => {
  refreshStateListeners.add(listener);
  return () => {
    refreshStateListeners.delete(listener);
  };
};

/**
 * Notify all listeners of refresh state change.
 * Called by customerAPI.ts when refresh starts/ends.
 */
export const notifyRefreshStateChange = (isRefreshing: boolean, error?: string | null): void => {
  refreshStateListeners.forEach((listener) => listener(isRefreshing, error));
};

/**
 * Hook that monitors token refresh state (Task #4377).
 * Provides reactive state for UI components to show refresh indicators.
 *
 * @returns Object with isRefreshing boolean and refreshError string or null
 */
export const useRefreshState = (): { isRefreshing: boolean; refreshError: string | null } => {
  const [isRefreshing, setIsRefreshing] = useState(isRefreshingToken);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  useEffect(() => {
    // Subscribe to refresh state changes
    const unsubscribe = subscribeToRefreshState((refreshing, error) => {
      setIsRefreshing(refreshing);
      if (error !== undefined) {
        setRefreshError(error);
      }
      // Clear error when refresh starts
      if (refreshing) {
        setRefreshError(null);
      }
    });

    // Listen for BroadcastChannel messages for cross-tab sync
    const handleAuthMessage = (event: MessageEvent) => {
      const { event: authEvent, data } = event.data || {};
      if (authEvent === AUTH_EVENTS.REFRESH_STARTED) {
        setIsRefreshing(true);
        setRefreshError(null);
      } else if (authEvent === AUTH_EVENTS.TOKEN_REFRESHED) {
        setIsRefreshing(false);
        setRefreshError(null);
      } else if (authEvent === AUTH_EVENTS.REFRESH_FAILED) {
        setIsRefreshing(false);
        setRefreshError(data?.error || 'Session refresh failed');
      } else if (authEvent === AUTH_EVENTS.REFRESH_CANCELLED) {
        // Task #4378: Refresh was cancelled by user - clear refreshing state, no error
        setIsRefreshing(false);
        setRefreshError(null);
      }
    };

    if (authChannel) {
      authChannel.addEventListener('message', handleAuthMessage);
    }

    return () => {
      unsubscribe();
      if (authChannel) {
        authChannel.removeEventListener('message', handleAuthMessage);
      }
    };
  }, []);

  return { isRefreshing, refreshError };
};

/**
 * Broadcasts an auth event to other tabs.
 * Used to synchronize logout and token refresh across tabs.
 */
export const broadcastAuthEvent = (event: string, data?: unknown): void => {
  // Use BroadcastChannel if available (more reliable)
  if (authChannel) {
    authChannel.postMessage({ event, data, timestamp: Date.now() });
  }
  // Also update localStorage to trigger storage event for fallback
  if (event === AUTH_EVENTS.LOGOUT) {
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }
};

/**
 * Hook that monitors customer token expiration and handles auto-logout.
 *
 * Features:
 * - Checks token expiration every 60 seconds
 * - Shows warning callback 5 minutes before expiry
 * - Auto-logs out user when token expires
 * - Handles clock skew with 30-second buffer
 * - Syncs across multiple browser tabs via storage event listener
 * - Detects sleep/wake with Page Visibility API
 * - Coordinates with token refresh to prevent race conditions
 *
 * @param onExpirationWarning - Callback when token will expire soon (5 min)
 * @param onExpired - Callback when token has expired (before logout)
 */
export const useTokenExpiration = (
  onExpirationWarning?: () => void,
  onExpired?: () => void
): void => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.customerAuth);

  // Track if warning has been shown to avoid duplicate notifications
  const warningShownRef = useRef(false);

  // Check token expiration status
  const checkExpiration = useCallback(() => {
    if (!isAuthenticated) {
      return;
    }

    // Don't check expiration while token refresh is in progress
    // This prevents race condition where we logout during refresh
    if (isRefreshingToken) {
      return;
    }

    // Always re-read from localStorage to catch updates from other tabs
    const expiryString = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryString) {
      // No expiry stored - can't check, rely on API 401 handling
      return;
    }

    const expiryTime = parseInt(expiryString, 10);
    if (isNaN(expiryTime)) {
      // Invalid expiry value - clear it
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      return;
    }

    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;

    // Token has expired (with buffer for clock skew)
    if (timeUntilExpiry <= EXPIRY_BUFFER_MS) {
      console.info('Token expired, logging out user');
      onExpired?.();
      dispatch(logoutCustomer());
      warningShownRef.current = false;
      return;
    }

    // Token will expire soon - show warning (only once per session)
    if (timeUntilExpiry <= WARNING_THRESHOLD_MS && !warningShownRef.current) {
      console.info(`Token expires in ${Math.round(timeUntilExpiry / 1000 / 60)} minutes`);
      warningShownRef.current = true;
      onExpirationWarning?.();
    }
  }, [isAuthenticated, dispatch, onExpirationWarning, onExpired]);

  // Set up periodic expiration check with multi-tab sync and sleep/wake detection
  useEffect(() => {
    if (!isAuthenticated) {
      // Reset warning flag when logged out
      warningShownRef.current = false;
      return;
    }

    // Check immediately on mount
    checkExpiration();

    // Set up interval for periodic checks
    const intervalId = setInterval(checkExpiration, CHECK_INTERVAL_MS);

    // Handle storage events from other tabs (multi-tab synchronization fallback)
    // When another tab updates the token expiry, re-check immediately
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === TOKEN_EXPIRY_KEY) {
        if (event.newValue === null) {
          // Token expiry was cleared (logout from another tab)
          dispatch(logoutCustomer());
          return;
        }
        // Another tab updated the token expiry - reset warning flag and re-check
        const newExpiry = parseInt(event.newValue, 10);
        const timeUntilExpiry = newExpiry - Date.now();
        // Only reset warning if new expiry is > warning threshold
        if (!isNaN(newExpiry) && timeUntilExpiry > WARNING_THRESHOLD_MS) {
          warningShownRef.current = false;
        }
        checkExpiration();
      }
    };

    // Handle BroadcastChannel messages from other tabs (preferred over storage events)
    const handleAuthMessage = (event: MessageEvent) => {
      const { event: authEvent, data } = event.data || {};
      if (authEvent === AUTH_EVENTS.LOGOUT) {
        // Another tab logged out - sync this tab
        dispatch(logoutCustomer());
      } else if (authEvent === AUTH_EVENTS.TOKEN_REFRESHED && data?.expiresIn) {
        // Token was refreshed in another tab - update local state
        const expiryTimestamp = Date.now() + data.expiresIn * 1000;
        localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiryTimestamp));
        const timeUntilExpiry = expiryTimestamp - Date.now();
        if (timeUntilExpiry > WARNING_THRESHOLD_MS) {
          warningShownRef.current = false;
        }
        checkExpiration();
      }
    };

    // Handle page visibility change (sleep/wake detection)
    // When user wakes laptop or switches back to tab, check immediately
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible - check immediately for wake-from-sleep scenarios
        checkExpiration();
      }
    };

    // Handle window focus (additional wake detection)
    const handleFocus = () => {
      checkExpiration();
    };

    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Add BroadcastChannel listener if available
    if (authChannel) {
      authChannel.addEventListener('message', handleAuthMessage);
    }

    // Cleanup on unmount or when auth state changes
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      if (authChannel) {
        authChannel.removeEventListener('message', handleAuthMessage);
      }
    };
  }, [isAuthenticated, checkExpiration, dispatch]);
};

/**
 * Utility function to store token expiration timestamp.
 * Call this after successful login with the expires_in value from the API.
 *
 * @param expiresInSeconds - Token lifetime in seconds (from API response)
 */
export const setTokenExpiry = (expiresInSeconds: number): void => {
  const expiryTimestamp = Date.now() + expiresInSeconds * 1000;
  localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiryTimestamp));
};

/**
 * Utility function to clear token expiration timestamp.
 * Call this on logout.
 */
export const clearTokenExpiry = (): void => {
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
};

/**
 * Utility function to check if token is expired without triggering logout.
 * Useful for checking before making API calls.
 *
 * @returns true if token is expired or will expire within buffer time
 */
export const isTokenExpired = (): boolean => {
  const expiryString = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryString) {
    // No expiry stored - assume not expired (rely on API)
    return false;
  }

  const expiryTime = parseInt(expiryString, 10);
  if (isNaN(expiryTime)) {
    return false;
  }

  return Date.now() >= expiryTime - EXPIRY_BUFFER_MS;
};

/**
 * Utility function to get time until token expires in milliseconds.
 *
 * @returns milliseconds until expiry, or null if no expiry stored
 */
export const getTimeUntilExpiry = (): number | null => {
  const expiryString = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryString) {
    return null;
  }

  const expiryTime = parseInt(expiryString, 10);
  if (isNaN(expiryTime)) {
    return null;
  }

  return Math.max(0, expiryTime - Date.now());
};

export default useTokenExpiration;
